/**
 * KEV dashboard — the customer/product overview.
 *
 * One KPI row and one grouped table per work section — Zu bewerten, Zu
 * benachrichtigen, Benachrichtigt (plus „Ohne Handlungsbedarf“ when there is
 * any). The operator chooses the grouping (`Nach Kunde` / `Nach Produkt`); a
 * group expands into its ProduktInstanzen of that section, an instance into its
 * components. Customer notifications are created by a
 * confirmed product assessment and sent from the Benachrichtigungen page
 * (DEV-100); this page
 * shows how far each instance is and links there.
 *
 * What it keeps from the React port of app/web/dashboard.html, and why:
 *
 *   - ONE state for BOTH payloads. `GET /api/dashboard/overview` alone cannot
 *     tell an unassessed hit from one whose product verdict is still open: the
 *     assessment progress lives in `GET /api/dashboard/vulnerability-events`.
 *     See `notifyGate`.
 *   - `only_affected` is the ONLY filter that hits the server; free text and
 *     the sections are client-side over the loaded rows, so typing never
 *     triggers a CMDBuild round trip.
 *   - Automatic refresh (60 s) — see useDashboardData for the stale-closure and
 *     cleanup details.
 *
 * Permissions (frozen vocabulary):
 *   dashboard.read     — read this page (the API enforces it),
 *   triage.read        — the triage links and the lazy KEV/EPSS lookup.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import Banner from '../components/Banner';
import EmptyState from '../components/EmptyState';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { useAuth } from '../lib/auth';
import { countLabel, fmtDate } from '../lib/format';
import type { OverviewRow, VulnerabilityEventRow } from '../lib/types';
import './dashboard/dashboard.css';
import {
  groupRows,
  sectionGroups,
  workState,
  type GroupMode,
  type WorkState,
} from './dashboard/dashboardGroups';
import { GroupTable } from './dashboard/GroupCells';
import {
  kpiCounters,
  matchesFilters,
  notifyGate,
  searchTokens,
  type NotifyGate,
} from './dashboard/dashboardModel';
import { useDashboardData } from './dashboard/useDashboardData';
import { useExploitability } from './dashboard/useExploitability';

/** Shown under every failed load — the two things that are actually wrong. */
const CMDB_HINT = 'Prüfen Sie die Verbindung zu CMDBuild und ob das Schema aktuell ist.';

/** Stable empty defaults so the memos below do not see a new array every render. */
const EMPTY_ROWS: readonly OverviewRow[] = [];
const EMPTY_EVENTS: readonly VulnerabilityEventRow[] = [];

/** The work sections in the order of the workflow. */
const SECTIONS: ReadonlyArray<{ state: WorkState; title: string; empty: string | null }> = [
  { state: 'assess', title: 'Zu bewerten', empty: 'Keine offenen Bewertungen' },
  { state: 'notify', title: 'Zu benachrichtigen', empty: 'Keine Benachrichtigung offen' },
  { state: 'notified', title: 'Benachrichtigt', empty: 'Noch keine Benachrichtigung versendet' },
  // Shown only when it has entries.
  { state: 'none', title: 'Ohne Handlungsbedarf', empty: null },
];

const sectionId = (state: WorkState): string => `dash-section-${state}`;

/** Remembered per viewer; storage may be unavailable (private mode, previews). */
const MODE_STORAGE_KEY = 'dashboard.groupMode';

function readMode(): GroupMode {
  try {
    return window.localStorage.getItem(MODE_STORAGE_KEY) === 'produkt' ? 'produkt' : 'kunde';
  } catch {
    return 'kunde';
  }
}

function writeMode(mode: GroupMode): void {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Only a convenience — the page works without it.
  }
}

export function DashboardPage(): ReactNode {
  const { has } = useAuth();
  const navigate = useNavigate();

  const canReadTriage = has('triage.read');

  const [mode, setModeState] = useState<GroupMode>(readMode);
  const [onlyAffected, setOnlyAffected] = useState(true);
  const [search, setSearch] = useState('');

  const setMode = useCallback((next: GroupMode) => {
    setModeState(next);
    writeMode(next);
  }, []);

  const { data, loading, error, syncStatus } = useDashboardData({ onlyAffected, paused: false });

  // KEV/EPSS facts are fetched only for rows the user expands, and only with the
  // permission the endpoint requires.
  const exploitability = useExploitability(canReadTriage);

  const rows = data?.overview.rows ?? EMPTY_ROWS;
  const events = data?.events.events ?? EMPTY_EVENTS;

  // The gate is a cross-payload join; compute it once per payload, not per cell.
  const gates = useMemo(() => {
    const map = new Map<number, NotifyGate>();
    for (const row of rows) map.set(row.produkt.id, notifyGate(row, events));
    return map;
  }, [rows, events]);

  const tokens = useMemo(() => searchTokens(search), [search]);
  const visibleRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, tokens)),
    [rows, tokens],
  );
  const sections = useMemo(() => {
    const groups = groupRows(visibleRows, mode, gates);
    return SECTIONS.map((section) => {
      const own = sectionGroups(groups, section.state, gates);
      return { ...section, groups: own, instances: own.reduce((sum, group) => sum + group.rows.length, 0) };
    });
  }, [visibleRows, mode, gates]);

  // Unfiltered by the search: the tile describes the loaded payload.
  const toNotify = useMemo(
    () => rows.filter((row) => workState(gates.get(row.produkt.id)) === 'notify').length,
    [rows, gates],
  );
  const counters = useMemo(
    () => (data === null ? [] : kpiCounters(data.overview.summary, data.events.offen, toNotify)),
    [data, toNotify],
  );

  // „Zu benachrichtigen“ jumps to its section. Open assessments live in the
  // triage queue (its default view), so that tile takes you there.
  const kpiTiles = useMemo(
    (): KpiItem[] =>
      counters.map((counter) => {
        if (counter.label === 'Offene Bewertungen') {
          return {
            ...counter,
            ariaLabel: `Offene Bewertungen: ${counter.value} in der Triage öffnen`,
            onClick: () => navigate('/triage'),
          };
        }
        if (counter.label === 'Zu benachrichtigen') {
          return {
            ...counter,
            ariaLabel: `Zu benachrichtigen: ${counter.value} anzeigen`,
            controls: sectionId('notify'),
            onClick: () =>
              document.getElementById(sectionId('notify'))?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          };
        }
        return counter;
      }),
    [counters, navigate],
  );

  /* --------------------------------------------------------------------- *
   * Render
   * --------------------------------------------------------------------- */

  const emptyText =
    rows.length === 0
      ? onlyAffected
        ? 'Keine Produktinstanzen mit KEV-Treffer. Deaktivieren Sie „Nur betroffene“, um alle anzuzeigen.'
        : 'Keine Produktinstanzen vorhanden. Unter „Stammdaten“ anlegen oder per CSV importieren.'
      : 'Nichts passt zur Suche.';
  const groupNoun = (count: number): string =>
    countLabel(count, mode === 'kunde' ? 'Kunde' : 'Produkt', mode === 'kunde' ? 'Kunden' : 'Produkte');

  return (
    <>
      <PageHeader
        title="Dashboard"
        sub={data === null ? undefined : `Stand ${fmtDate(data.overview.generated_at)}`}
        actions={loading && data !== null ? <Spinner label="Daten werden geladen…" /> : null}
      />

      {syncStatus !== null ? (
        <Banner kind={syncStatus === 'retrying' ? 'warn' : 'info'}>
          {syncStatus === 'retrying'
            ? 'Die Übertragung an Dependency-Track ist fehlgeschlagen und wird automatisch wiederholt.'
            : 'Änderungen werden an Dependency-Track übertragen. Ergebnisse folgen nach der Analyse.'}
        </Banner>
      ) : null}

      {error !== null ? (
        <Banner kind="error" sub={CMDB_HINT}>
          Übersicht konnte nicht geladen werden: {error}
        </Banner>
      ) : null}

      {data === null ? (
        loading ? (
          <PageSpinner text="Übersicht wird geladen…" />
        ) : (
          <EmptyState title="Keine Daten geladen" />
        )
      ) : (
        <>
          <KpiRow counters={kpiTiles} label="Kennzahlen zur aktuellen Sicherheitslage" />

          <div className="card dash-filter">
            <div className="table-toolbar">
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder="Kunde, Produkt, Komponente oder CVE suchen…"
                aria-label="Übersicht filtern"
              />
              <div className="table-toolbar-options">
                <label className="check" title="Serverseitiger Filter: lädt die Übersicht neu">
                  <input
                    type="checkbox"
                    checked={onlyAffected}
                    onChange={(event) => setOnlyAffected(event.currentTarget.checked)}
                  />
                  Nur betroffene
                </label>
                <div className="seg view-switch" role="group" aria-label="Gruppierung wählen">
                  <button
                    type="button"
                    className={mode === 'kunde' ? 'on' : undefined}
                    aria-pressed={mode === 'kunde'}
                    onClick={() => setMode('kunde')}
                  >
                    Nach Kunde
                  </button>
                  <button
                    type="button"
                    className={mode === 'produkt' ? 'on' : undefined}
                    aria-pressed={mode === 'produkt'}
                    onClick={() => setMode('produkt')}
                  >
                    Nach Produkt
                  </button>
                </div>
              </div>
            </div>
          </div>

          {visibleRows.length === 0 ? (
            <section className="card dash-worklist">
              <EmptyState title={emptyText} />
            </section>
          ) : (
            sections
              .filter((section) => section.groups.length > 0 || section.empty !== null)
              .map((section) => (
                <section
                  key={section.state}
                  id={sectionId(section.state)}
                  className={`card dash-worklist dash-section${section.groups.length === 0 ? ' is-empty' : ''}`}
                  aria-labelledby={`${sectionId(section.state)}-title`}
                >
                  <div className="card-head table-head">
                    <h2 id={`${sectionId(section.state)}-title`}>{section.title}</h2>
                    <span className="table-count">
                      {section.groups.length === 0
                        ? section.empty
                        : `${groupNoun(section.groups.length)} · ${countLabel(section.instances, 'Auslieferung', 'Auslieferungen')}`}
                    </span>
                  </div>
                  {section.groups.length > 0 ? (
                    <GroupTable
                      mode={mode}
                      section={section.state}
                      caption={section.title}
                      groups={section.groups}
                      store={exploitability}
                      gates={gates}
                      canReadTriage={canReadTriage}
                      emptyText={emptyText}
                      paginationResetKey={JSON.stringify([mode, onlyAffected, search, section.state])}
                    />
                  ) : null}
                </section>
              ))
          )}
        </>
      )}

    </>
  );
}

export default DashboardPage;
